import { createContext, useContext } from 'react';

import type { JournalDetail } from './useAddEditJournalDetailQuery';

export type EditJournalRouteState = {
  onCreateAction?:
    | 'duplicate'
    | 'goToJournalsList'
    | 'newJournal'
    | 'newRecurringJournal'
    | 'reverse'
    | 'setJournalAsRecurring';
};

export type CreateJournalRouteState = {
  journalToDuplicate?: JournalDetail;
  journalToReverse?: JournalDetail;
  recurringJournalId?: string;
};

export interface JournalsContextValue {
  currentURLHashWithBookId: string;
  navigateToEditJournal: (args: {
    journalId: string;
    editJournalRouteState?: EditJournalRouteState;
  }) => void;
  buildJournalHref: (journalId: string) => string;
  /**
   * Only set while the React recurring journal page is reachable. Recurring
   * journals have no classic page to fall back to, so the list leaves the
   * template name as plain text when it is unset.
   */
  buildRecurringJournalHref?: (recurringJournalId: string) => string;
  /**
   * Only hosts that show real journals need these — a journal line item can link
   * to an invoice or a pay run. Recurring journals are templates and never carry
   * linked transactions, so that host leaves them unset.
   */
  buildInvoiceHref?: (invoiceId: string) => string;
  buildPayrunHref?: (payrunId: string) => string;
  navigateToCreateJournal: (args?: {
    createJournalRouteState?: CreateJournalRouteState;
  }) => void;
  navigateToCreateJournalFromRecurringJournal: (args: {
    recurringJournalId: string;
  }) => void;
  /**
   * Only set while the React recurring journal page is reachable. Journals have
   * no classic "set as recurring" page to fall back to, so the action is hidden
   * when it is unset.
   */
  navigateToCreateRecurringJournalFromJournal?: (args: {
    journal: JournalDetail;
  }) => void;
  navigateToJournalsList: () => void;
  navigateToRecurringTransactionsList?: () => void;
  breadcrumbs: Array<{ pathname: string; breadcrumbTitle: string }>;
  journalHelpUrl: string;
  stage: string;
  isFastModeEnabled: boolean;
  moreLinks: Array<{ label: string; url: string }>;
  storageKeyPrefix: string;
}

export const JournalsContext = createContext<JournalsContextValue | null>(null);

export function useJournalsContext(): JournalsContextValue {
  const context = useContext(JournalsContext);
  if (!context) {
    throw new Error(
      'useJournalsContext must be used within a JournalsProvider'
    );
  }
  return context;
}
